import { Link } from "react-router-dom";
import {
  Route, Car, Layers, Wrench, Cone, Slash, SquareParking,
  Footprints, CalendarCheck, Shovel, Phone, MessageCircle,
} from "lucide-react";
import { SITE, waLink } from "@/config/site";
import { usePageTitle, Reveal, SectionHead, Eyebrow, RoadDash } from "@/components/bits";
import { QuoteCallout } from "./Home";

const HERO_IMG = "/work/parking-lot.jpg";

const SERVICES = [
  { icon: Route, title: "Asphalt Paving", copy: "New asphalt surfaces laid properly — driveways, parking areas, pathways and more, built to handle Bermuda weather." },
  { icon: Car, title: "Driveways", copy: "Driveway paving that lifts the look of your property from the moment you pull up." },
  { icon: Layers, title: "Asphalt Resurfacing", copy: "A fresh wearing course over a sound base — a cost-effective way to make old asphalt new again." },
  { icon: Wrench, title: "Asphalt Patching", copy: "Damaged sections cut out and replaced cleanly so problems don't spread." },
  { icon: Cone, title: "Pothole Repair", copy: "Potholes filled and repaired properly before they grow into expensive failures." },
  { icon: Slash, title: "Crack Repair", copy: "Cracks sealed early to keep water out and stop small issues becoming big ones." },
  { icon: SquareParking, title: "Parking Areas", copy: "Paving, resurfacing and maintenance for residential and commercial parking areas." },
  { icon: Footprints, title: "Walkways", copy: "Smooth, safe asphalt walkways and paths for homes and businesses." },
  { icon: CalendarCheck, title: "Asphalt Maintenance", copy: "Ongoing preventative maintenance to get the longest life from your surfaces." },
  { icon: Shovel, title: "Surface Preparation", copy: "Grading, base work and preparation done right — the foundation of asphalt that lasts." },
];

export default function Asphalt() {
  usePageTitle("Asphalt Services");
  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-white/10" data-testid="asphalt-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-joell-black/70 via-joell-black/60 to-joell-black" aria-hidden="true" />
        <div className="container-x relative pb-20 pt-36 md:pb-28 md:pt-48">
          <Reveal>
            <Eyebrow>Bermuda Asphalt Company</Eyebrow>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl">
              Asphalt services built to last
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Whether you need a new driveway, asphalt repairs or ongoing maintenance, JOELL provides dependable
              asphalt solutions for properties throughout Bermuda.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/request-quote" className="btn-accent !px-8 !py-4" data-testid="asphalt-quote-button">Request a Free Quote</Link>
              <a href={SITE.phoneHref} className="btn-outline !px-8 !py-4" data-testid="asphalt-call-button"><Phone size={15} /> Call {SITE.phone}</a>
            </div>
          </Reveal>
        </div>
        <div className="absolute inset-x-0 bottom-0"><RoadDash /></div>
      </section>

      <section className="container-x py-20 md:py-28" data-testid="asphalt-services-section">
        <SectionHead
          eyebrow="What we do"
          title="Every asphalt job, done properly"
          sub="From full driveways to single potholes — no job too big or too small."
        />
        <div className="grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.04}>
              <div className="group h-full bg-joell-black p-7 transition-colors duration-300 hover:bg-joell-surface md:p-8" data-testid={`asphalt-service-card-${i}`}>
                <div className="flex items-start justify-between">
                  <s.icon size={26} strokeWidth={1.6} className="text-joell-yellow" />
                  <span className="font-mono text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-7 font-display text-xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 border border-white/10 bg-joell-coal/60 p-6 text-sm text-slate-400">
            Before-and-after photos of recent asphalt projects are shown in{" "}
            <Link to="/our-work" className="text-joell-yellow underline underline-offset-4">Our Work</Link> — and
            you can always <Link to="/request-quote" className="text-joell-yellow underline underline-offset-4">send us photos</Link> of your own project.
          </p>
        </Reveal>
      </section>

      <section className="container-x pb-20 md:pb-28">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 border border-joell-yellow/40 bg-joell-yellow/5 p-8 sm:p-10 md:flex-row md:items-center" data-testid="asphalt-cta">
            <div>
              <h2 className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">Need asphalt work?</h2>
              <p className="mt-2 text-slate-300">Request your free quote today.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/request-quote" className="btn-accent" data-testid="asphalt-cta-quote-button">Get My Free Quote</Link>
              <a href={waLink("Hi JOELL, I need some asphalt work done.")} target="_blank" rel="noreferrer" className="btn-outline" data-testid="asphalt-cta-whatsapp-button">
                <MessageCircle size={15} /> WhatsApp
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <QuoteCallout />
    </>
  );
}
